import { useEffect, useRef, useState } from 'react';
import * as wasm from 'sched';
import { useDatabase } from './useDatabase';

export interface UseDatabaseEntityOptions<T> {
  /**
   * Display name used in the not-found error ("X with ID Y not found"). Lower-cased
   * automatically for the generic-failure message.
   */
  entityType: string;

  /**
   * Build a typed view of the entity from the WASM database.
   *
   * - Return the built object on success.
   * - Return `null` to signal "not found" — the helper translates this to a
   *   user-facing "X with ID Y not found" error.
   * - Throw to signal an unexpected fetch failure — the helper translates
   *   this to a generic error and logs the underlying exception.
   *
   * Callers that want to fall back to partial data on async-enrichment failure
   * should catch internally and return the partial object — the helper treats
   * any non-null return as success.
   */
  build: (database: wasm.WrappedDatabase, id: string) => Promise<T | null> | T | null;

  /**
   * Extra reactive dependencies for the build closure. The effect re-runs when
   * any of these change. Use this for closure-captured values like `safeData`
   * that aren't in the base `(database, id)` dependency set.
   */
  deps?: ReadonlyArray<unknown>;
}

export interface UseDatabaseEntityResult<T> {
  entity: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Common state machine for the entity-by-id detail hooks (`useTask`,
 * `useOutcome`, `useTeam`, etc).
 *
 * Loading sequencing — the subtle bit:
 *
 *   The original hooks called `setLoading(false)` in a synchronous `finally`
 *   even when their success path fired an unawaited async builder. That left
 *   a render where `loading=false` and `entity=null`, and the typical detail
 *   page renders that as "not found" before the data arrives. This helper
 *   only flips `loading` false once we've reached a terminal state (entity
 *   populated, genuinely not found, or build threw).
 */
export function useDatabaseEntity<T>(
  id: string | undefined,
  options: UseDatabaseEntityOptions<T>,
): UseDatabaseEntityResult<T> {
  const { entityType, build, deps = [] } = options;
  const { database, loading: databaseLoading } = useDatabase();
  const [entity, setEntity] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hold the latest build closure so we don't re-run the effect just because
  // the caller passed an inline closure. The effect re-runs only on changes
  // to `database`, `id`, `entityType`, or caller-provided `deps`.
  const buildRef = useRef(build);
  buildRef.current = build;

  useEffect(() => {
    if (!database || !id) {
      // Stay in loading; `databaseLoading` covers the no-database case via
      // the returned `loading` below. If the route hasn't supplied an id yet,
      // the page is still mounting — the parent will resolve it shortly.
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.resolve()
      .then(() => buildRef.current(database, id))
      .then((result) => {
        if (cancelled) return;
        if (result === null || result === undefined) {
          setEntity(null);
          setError(`${entityType} with ID ${id} not found`);
        } else {
          setEntity(result);
          setError(null);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(`Error fetching ${entityType.toLowerCase()}:`, err);
        setEntity(null);
        setError(`Failed to fetch ${entityType.toLowerCase()} details`);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `build` is intentionally omitted — held in a ref to allow inline closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [database, id, entityType, ...deps]);

  return { entity, loading: loading || databaseLoading, error };
}
